import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { Box, useTheme, useMediaQuery } from '@mui/material';

import ThemeVisualizer from "./pages/ThemeVisualizer";
import Header from "./components/header";
import Home from './pages';
import SidePanel from "./components/sidePanel";
import Bookmarks from "./pages/Bookmarks";

import { useAuth } from "./hooks/useAuth";
import { decodeJWT } from "./utils/commonUtils";
import VerifyEmail from "./components/auth/VerifyEmail";
import ResetPassword from "./components/auth/ResetPassword";
// import Settings from "./pages/Settings";
import NotFound from "./pages/NotFound";
import FAQs from "./pages/FAQs";
import TermsPage from "./pages/TermsPage";
// import BookmarkSearch from "./pages/BookmarkSearch";

// Components for Non-Logged-in Users
import LandingPage from "./pages/LandingPage";
import About from "./pages/About";
import Services from "./pages/Services";
import Contact from "./pages/Contact";
import Support from "./pages/Support";
// import Pricing from "./pages/Pricing";
import PrivacyPolicy from "./pages/PrivacyPolicy";


const checkSidePanelOpenStatus = () => {
  const storedSidePanelOpen = localStorage.getItem("sidePanelOpen");
  return storedSidePanelOpen ? JSON.parse(storedSidePanelOpen) : true;
};

const App = () => {
  const theme = useTheme();
  const isMediumOrSmaller = useMediaQuery(theme.breakpoints.down('md'));
  const [sidePanelOpen, setSidePanelOpen] = useState(checkSidePanelOpenStatus);
  const { authData } = useAuth();  // Get the auth data using a custom hook or context

  let userDetails = null;

  // Decode the access token to extract user information
  if (authData?.accessToken) {
    try {
      userDetails = decodeJWT(authData.accessToken);
    } catch (error) {
      console.error("Error decoding access token:", error);
      // Optionally handle invalid tokens here
    }
  }

  useEffect(() => {
    if (isMediumOrSmaller) {
      setSidePanelOpen(false); // Ensure hidden on smaller screens initially
    }
  }, [isMediumOrSmaller]);

  const togglePanel = () => {
    const sidePanelOpenFlag = !sidePanelOpen;
    setSidePanelOpen(sidePanelOpenFlag);
    localStorage.setItem("sidePanelOpen", JSON.stringify(sidePanelOpenFlag));
  };

  // Function to render the routes for non-logged-in users
  const renderPublicRoutes = () => (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/home" element={<LandingPage />} />
      <Route path="/about" element={<About />} />
      {/* <Route path="/services" element={<Services />} /> */}
      <Route path="/contact" element={<Contact />} />
      <Route path="/faq" element={<FAQs />} />
      {/* <Route path="/pricing" element={<Pricing />} /> */}
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/verify/:uid/:token" element={<VerifyEmail />} />
      <Route path="/reset/:uid/:token" element={<ResetPassword />} />
      <Route path="*" element={<NotFound />} />
      {/* Add other public routes here like login, signup, etc. */}
    </Routes>
  );

  // Function to render the routes for logged-in users
  const renderAuthenticatedRoutes = () => (
    <React.Fragment>
      <SidePanel sidePanelOpen={sidePanelOpen} togglePanel={togglePanel} />
      <Box sx={{
        flexGrow: 1,
        padding: theme.spacing(1),
        overflow: 'auto',
        display: "flex",
      }}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="bookmarks/" element={<Bookmarks />} />
          <Route path="theme/" element={<ThemeVisualizer />} />
          {/* <Route path="settings/" element={<Settings />} /> */}
          <Route path="support/" element={<Support />} />
          <Route path="faq/" element={<FAQs />} />
          <Route path="terms/" element={<TermsPage />} />
          <Route path="privacy/" element={<PrivacyPolicy />} />
          {/* <Route path="/verify/:uid/:token" element={<VerifyEmail />} /> */}
          {/* <Route path="/reset/:uid/:token" element={<ResetPassword />} /> */}
          {/* <Route path="/search" element={<BookmarkSearch />} /> */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Box>
    </React.Fragment>
  );

  return (
    <Router> {/* Make sure Router wraps the entire routing logic */}
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
        <Header />
        <Box sx={{
          display: 'flex',
          flexGrow: 1,
          overflow: 'hidden',
          backgroundColor: "background.default",
        }}>
          {/* Conditional rendering based on whether the user is logged in */}
          {userDetails && userDetails.name && userDetails.email ? renderAuthenticatedRoutes() : renderPublicRoutes()}
        </Box>
      </Box>
    </Router>
  );
};

export default App;
